/**
 * Install-prompt hook (contract §7).
 *
 * - Android/desktop Chrome: captures `beforeinstallprompt`; `canInstall`
 *   becomes true and `promptInstall()` opens the native install dialog.
 * - iOS Safari: never fires that event — use `isIosDevice()` together with
 *   `isStandalone()` to show manual guidance ("Deel → Zet op beginscherm")
 *   in guidance UIs such as the InstallPage.
 *
 * The event can fire before React mounts, so the capture listener lives at
 * module scope and installs lazily on first use.
 */
import { useCallback, useEffect, useState } from 'react'

/** Minimal shape of the non-standard beforeinstallprompt event. */
interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[]
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>
  prompt(): Promise<void>
}

let deferredPrompt: BeforeInstallPromptEvent | null = null
let listening = false
const subscribers = new Set<() => void>()

function notifySubscribers() {
  subscribers.forEach((callback) => callback())
}

function ensureInstallCapture() {
  if (listening || typeof window === 'undefined') return
  listening = true

  window.addEventListener('beforeinstallprompt', (event) => {
    // We drive the prompt ourselves from the install UI.
    event.preventDefault()
    deferredPrompt = event as BeforeInstallPromptEvent
    notifySubscribers()
  })

  window.addEventListener('appinstalled', () => {
    deferredPrompt = null
    notifySubscribers()
  })
}

/** True on iPhone/iPad/iPod (including iPadOS reporting as macOS). */
export function isIosDevice(): boolean {
  if (typeof navigator === 'undefined') return false
  const ua = navigator.userAgent
  if (/iPad|iPhone|iPod/.test(ua)) return true
  // iPadOS 13+ sends a macOS user agent but exposes touch points.
  return navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1
}

/** True when the app runs installed (standalone / display-mode standalone). */
export function isStandalone(): boolean {
  if (typeof window === 'undefined') return false
  if (window.matchMedia('(display-mode: standalone)').matches) return true
  // iOS Safari legacy flag.
  const nav = navigator as Navigator & { standalone?: boolean }
  return nav.standalone === true
}

export interface InstallPromptState {
  /** A native install prompt is ready to be shown (Android/desktop). */
  canInstall: boolean
  /** Open the native install prompt; resolves after the user chooses. */
  promptInstall(): Promise<void>
  /** iOS device — show manual "Zet op beginscherm" guidance instead. */
  isIos: boolean
  /** Already running as an installed app. */
  isInstalled: boolean
}

export function useInstallPrompt(): InstallPromptState {
  ensureInstallCapture()
  const [canInstall, setCanInstall] = useState(() => deferredPrompt !== null)

  useEffect(() => {
    const sync = () => setCanInstall(deferredPrompt !== null)
    subscribers.add(sync)
    sync()
    return () => {
      subscribers.delete(sync)
    }
  }, [])

  const promptInstall = useCallback(async () => {
    if (!deferredPrompt) return
    await deferredPrompt.prompt()
    const { outcome } = await deferredPrompt.userChoice
    // The event is single-use; an accepted or dismissed prompt clears it.
    deferredPrompt = null
    setCanInstall(false)
    if (outcome === 'accepted') notifySubscribers()
  }, [])

  return {
    canInstall,
    promptInstall,
    isIos: isIosDevice(),
    isInstalled: isStandalone(),
  }
}
